import React, { useImperativeHandle, forwardRef } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import { GET_WALLET, GET_WALLET_STATS } from "../graphql/wallets";
import { WalletBalanceHistoryComponent } from "./WalletBalanceHistory";
import { WalletActivitiesComponent } from "./WalletActivities";

interface WalletDetailsProps {
  walletId: string;
}

const WalletDetails = forwardRef<any, WalletDetailsProps>(
  ({ walletId }, ref) => {
    const {
      data: walletData,
      loading: walletLoading,
      error: walletError,
      refetch: refetchWallet,
    } = useQuery(GET_WALLET, {
      variables: { id: walletId },
      skip: !walletId,
    });
    const {
      data: statsData,
      loading: statsLoading,
      refetch: refetchStats,
    } = useQuery(GET_WALLET_STATS, {
      variables: { id: walletId },
      skip: !walletId,
    });

    useImperativeHandle(ref, () => ({
      refetchWallet: async () => {
        await refetchWallet();
        await refetchStats();
      },
    }));

    if (!walletId) {
      return <Typography variant="body1">No wallet selected.</Typography>;
    }
    if (walletLoading || statsLoading) {
      return <CircularProgress />;
    }
    if (walletError) {
      return (
        <Typography color="error">
          Error loading wallet: {walletError.message}
        </Typography>
      );
    }
    if (!walletData?.wallet) {
      return <Typography>No wallet found.</Typography>;
    }

    const wallet = walletData.wallet;
    const stats = statsData?.walletStats?.stats;

    return (
      <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={4}
          alignItems="flex-start"
        >
          <Box flex={2}>
            <Typography variant="h5" gutterBottom>
              {wallet.name}{" "}
              <Chip label={wallet.type} size="small" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Status: <Chip label={wallet.status} size="small" />
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Balance:{" "}
              <b>
                {wallet.balance.toLocaleString()} {wallet.currency}
              </b>
            </Typography>
            {wallet.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {wallet.description}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Created: {new Date(wallet.createdAt).toLocaleString()} | Updated:{" "}
              {new Date(wallet.updatedAt).toLocaleString()}
            </Typography>
          </Box>
          <Box flex={1} minWidth={200}>
            {stats && (
              <Box>
                <Typography variant="subtitle2">Stats</Typography>
                <Typography variant="body2">
                  Total Trades: {stats.totalTrades}
                </Typography>
                <Typography variant="body2">
                  Open Positions: {stats.openPositions}
                </Typography>
                <Typography variant="body2">
                  Total Positions: {stats.totalPositions}
                </Typography>
                <Typography variant="body2">
                  Current Balance: {stats.currentBalance.toLocaleString()}{" "}
                  {wallet.currency}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        {/* Recent Activity */}
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <WalletActivitiesComponent walletId={walletId} limit={5} />
        <Divider sx={{ my: 2 }} />
        {/* Balance Change History */}
        <Typography variant="h6" gutterBottom>
          Balance Change History
        </Typography>
        <WalletBalanceHistoryComponent walletId={walletId} />
      </Paper>
    );
  },
);

export default WalletDetails;
